import { Injectable } from "@angular/core";
import { catchError, Observable, throwError } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ChatResponse, AiChatbotResponse, AsyncInvokePayload, AsyncInvokeResponse, JobStatusResponse, AsyncResultResponse, ExecuteSqlPayload, ExecuteSqlResponse } from "../interface/scraii-ai-interface";
import { environment } from "src/environments/environment";
import { friendlyApiErrorMessage } from "src/app/services/api-error-notifier.service";

@Injectable({
  providedIn: "root",
})
export class AiChatServiceService {
  constructor(private http: HttpClient) { }

  // ✅ Old API endpoint
  private readonly API_URL = environment.aiChatEndPoint;

  // ✅ New AI Chatbot endpoint
  private readonly AI_CHATBOT_URL = environment.aiChatbotEndPoint;

  // Async API endpoints
  private readonly ASYNC_INVOKE_URL = environment.aiAsyncInvokeEndPoint;
  private readonly ASYNC_POLL_URL = environment.aiAsyncPollEndPoint;
  private readonly EXECUTE_SQL_URL = environment.aiExecuteSqlEndPoint;
  private readonly  AI_ACTION_SQL_URL = environment.aiActionSqlEndPoint;

  sendMessage(prompt: string): Observable<ChatResponse> {
    const payload = { prompt }; // ✅ Payload
    return this.http.post<ChatResponse>(this.API_URL, payload).pipe(
      catchError((err) => {
        console.error("ChatService Error:", err);
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't send your message. Please try again."))
        );
      })
    );
  }

  // ✅ New method for AI Chatbot endpoint
  sendChatbotMessage(
    user_id: string,
    prompt: string,
    conversation_id: string
  ): Observable<AiChatbotResponse> {
    const payload = { user_id, prompt, conversation_id };

    return this.http.post<AiChatbotResponse>(this.AI_CHATBOT_URL, payload).pipe(
      catchError((err) => {
        console.error("AI Chatbot Service Error:", err);
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't send your message. Please try again."))
        );
      })
    );
  }

  getChatMessages(conversation_id: string, user_id?: string): Observable<any> {
    const payload = { conversation_id };
    return this.http.post<any>(this.AI_CHATBOT_URL, payload).pipe(
      catchError((err) => {
        console.error("Get Chat Messages Error:", err);
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't load these messages. Please try again."))
        );
      })
    );
  }

  // Execute SQL query and get results
  testSqlQuery(payload: { sqlQuery: string }): Observable<any> {
    const testSqlUrl = environment.apiBaseUrl + "/pss/get-ai-data";
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });
    return this.http.post(testSqlUrl, payload, { headers }).pipe(
      catchError((err) => {
        console.error("Test SQL Query Error:", err);

        // The backend's own text is not forwarded: on this endpoint it is raw
        // database output (ORA codes, column and table names) and the portal
        // never shows that to a user. friendlyApiErrorMessage picks the portal's
        // wording for the status — the raw status line this used to throw
        // ("504 Gateway Timeout") named the failure to the one person who could
        // do nothing about it.
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't run that query. Please try again."))
        );
      })
    );
  }

  putFeedback(data: {
    prompt: string;
    feedback: string;
    sql_query: string;
    suggested_sql_query: string;
    conversation_id: string;
    message_id: string;
    user_id?: string;
  }): Observable<any> {
    return this.http.put<any>(this.API_URL, data).pipe(
      catchError((err) => {
        console.error("Feedback Error:", err);
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't send your feedback. Please try again."))
        );
      })
    );
  }

  loadChatHistory(user_id: string): Observable<any> {
    const payload = { user_id };
    return this.http.post<any>(this.AI_CHATBOT_URL, payload).pipe(
      catchError((err) => {
        console.error("Load Chat History Error:", err);
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't load your chat history. Please try again."))
        );
      })
    );
  }

  /**
   * Record a reviewer's verdict on one cached query — Approve / Reject on the SQL
   * Cache Review dashboard.
   *
   * Same action-dispatch shape as deleteConversation below: the chatbot lambda
   * routes on `action`, so the verdict rides the chat API rather than the portal's
   * own, which is where the cache it grades lives.
   *
   * `message_timestamp` is optional on the endpoint but sent whenever the row
   * carries one — it is what lets the backend identify the exact message when a
   * conversation holds more than one answer to the same question.
   *
   * `corrected_sql` rides the same approve call rather than an endpoint of its own:
   * a reviewer filing a correction IS approving the message, with the query they
   * would rather the cache held. Sent only when a correction was actually typed, so
   * a plain Approve posts exactly the payload it always did — an undefined field is
   * dropped on the way out rather than posted as null.
   */
  submitCacheReview(data: {
    conversation_id: string;
    message_id: string;
    operation: "approve" | "reject" | "correct";
    user_id: string;
    message_timestamp?: string;
    corrected_sql?: string;
  }): Observable<any> {
    const payload = { action: "cache_review", ...data };
    return this.http.post<any>(this.AI_ACTION_SQL_URL, payload).pipe(
      catchError((err) => {
        console.error("Cache Review Error:", err);
        return throwError(() => new Error("Failed to record the review."));
      })
    );
  }

  // Delete a conversation
  deleteConversation(conversation_id: string): Observable<any> {
    const payload = { conversation_id, action: "delete" };
    return this.http.post<any>(this.AI_CHATBOT_URL, payload).pipe(
      catchError((err) => {
        console.error("Delete Conversation Error:", err);
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't delete this conversation. Please try again."))
        );
      })
    );
  }

  // Async API methods
  submitAsyncJob(payload: AsyncInvokePayload): Observable<AsyncInvokeResponse> {
    return this.http.post<AsyncInvokeResponse>(this.ASYNC_INVOKE_URL, payload).pipe(
      catchError((err) => {
        console.error("Async Job Submit Error:", err);
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't start that request. Please try again."))
        );
      })
    );
  }

  pollJobStatus(jobid: string): Observable<JobStatusResponse> {
    return this.http.post<JobStatusResponse>(this.ASYNC_POLL_URL, { jobid }).pipe(
      catchError((err) => {
        console.error("Poll Job Status Error:", err);
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't check on that request. Please try again."))
        );
      })
    );
  }

  fetchPresignedResult(url: string): Observable<AsyncResultResponse> {
    return this.http.get<AsyncResultResponse>(url).pipe(
      catchError((err) => {
        console.error("Fetch Presigned Result Error:", err);
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't load the result. Please try again."))
        );
      })
    );
  }

  // Replacement for fetchPresignedResult — fetch job result directly by job_id
  getJobResults(job_id: string): Observable<AsyncResultResponse> {
    const url = environment.apiBaseUrl + "/frmportal/get-job-results";
    return this.http.post<AsyncResultResponse>(url, { job_id }).pipe(
      catchError((err) => {
        console.error("Get Job Results Error:", err);
        return throwError(() => new Error("Failed to fetch job results."));
      })
    );
  }

  executeSql(payload: ExecuteSqlPayload): Observable<ExecuteSqlResponse> {
    return this.http.post<ExecuteSqlResponse>(this.EXECUTE_SQL_URL, payload).pipe(
      catchError((err) => {
        console.error("Execute SQL Error:", err);
        return throwError(
          () => new Error(friendlyApiErrorMessage(err, "We couldn't run that query. Please try again."))
        );
      })
    );
  }
  
  // Replacement for fetchPresignedResult — fetch job result directly by job_id
  // getJobResults(job_id: string): Observable<AsyncResultResponse> {
  //   const url = environment.apiBaseUrl + "/frmportal/get-job-results";
  //   return this.http.post<AsyncResultResponse>(url, { job_id }).pipe(
  //     catchError((err) => {
  //       console.error("Get Job Results Error:", err);
  //       return throwError(() => new Error("Failed to fetch job results."));
  //     })
  //   );
  // }
}
